import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useSessionStore } from '../stores/session.js'

/**
 * Один SPA на два раздела (docs/02 §2.1):
 *   /app/*   — кабинет менеджера
 *   /admin/* — админка владельца сервиса
 *
 * Доступ проверяется по `/auth/me`: сессия лежит в httpOnly-cookie, поэтому
 * фронтенд не может проверить её сам — спрашивает сервер один раз на загрузку.
 *
 * Второй фактор (этап 3г): если сессия половинчатая, кабинет пускает только
 * на `/totp`. Это не «нет входа» — человек уже опознан, — поэтому отдельная
 * страница, а не возврат на `/login`.
 */
const routes: RouteRecordRaw[] = [
  { path: '/', redirect: '/app' },
  {
    path: '/login',
    name: 'login',
    component: () => import('../pages/LoginPage.vue'),
    meta: { public: true },
  },
  {
    /**
     * Страница показа демо-бота (этап 4г): открыта без входа, ссылку
     * отправляют потенциальному клиенту.
     */
    path: '/demo/:key',
    name: 'demo',
    component: () => import('../pages/DemoPage.vue'),
    meta: { public: true, anonymous: true },
  },
  {
    path: '/totp',
    name: 'totp',
    component: () => import('../pages/TotpPage.vue'),
    meta: { totp: true },
  },
  {
    path: '/app',
    component: () => import('../layouts/CabinetLayout.vue'),
    children: [
      // Вход в кабинет — по-прежнему «Чаты»: оператор открывает кабинет,
      // чтобы отвечать людям, а не смотреть графики (этап 5а не меняет
      // привычку, «Обзор» стоит первым в меню).
      { path: '', redirect: '/app/chats' },
      {
        path: 'overview',
        name: 'overview',
        component: () => import('../pages/app/OverviewPage.vue'),
      },
      { path: 'chats', name: 'chats', component: () => import('../pages/app/ChatsPage.vue') },
      { path: 'leads', name: 'leads', component: () => import('../pages/app/LeadsPage.vue') },
      { path: 'bot', name: 'bot', component: () => import('../pages/app/BotPage.vue') },
      {
        path: 'knowledge',
        name: 'knowledge',
        component: () => import('../pages/app/KnowledgePage.vue'),
      },
      { path: 'widget', name: 'widget', component: () => import('../pages/app/WidgetPage.vue') },
      {
        path: 'integrations',
        name: 'integrations',
        component: () => import('../pages/app/IntegrationsPage.vue'),
      },
      {
        path: 'canned-replies',
        name: 'canned-replies',
        component: () => import('../pages/app/CannedRepliesPage.vue'),
      },
      { path: 'plan', name: 'plan', component: () => import('../pages/app/PlanPage.vue') },
      {
        path: 'support',
        name: 'support',
        component: () => import('../pages/app/SupportPage.vue'),
      },
      {
        path: 'security',
        name: 'security',
        component: () => import('../pages/app/SecurityPage.vue'),
      },
      {
        path: 'settings',
        name: 'settings',
        component: () => import('../pages/app/SettingsPage.vue'),
      },
      {
        /** Партнёрский кабинет (этап 6е): список своих клиентов. */
        path: 'partner',
        name: 'partner',
        component: () => import('../pages/app/PartnerPage.vue'),
      },
    ],
  },
  {
    path: '/admin',
    component: () => import('../layouts/AdminLayout.vue'),
    children: [
      { path: '', redirect: '/admin/overview' },
      {
        path: 'overview',
        name: 'admin-overview',
        component: () => import('../pages/admin/OverviewPage.vue'),
      },
      {
        path: 'tenants',
        name: 'admin-tenants',
        component: () => import('../pages/admin/TenantsPage.vue'),
      },
      {
        path: 'tenants/:id',
        name: 'admin-tenant',
        component: () => import('../pages/admin/TenantPage.vue'),
      },
      {
        path: 'plans',
        name: 'admin-plans',
        component: () => import('../pages/admin/PlansPage.vue'),
      },
      {
        path: 'usage',
        name: 'admin-usage',
        component: () => import('../pages/admin/UsagePage.vue'),
      },
      { path: 'logs', name: 'admin-logs', component: () => import('../pages/admin/LogsPage.vue') },
      {
        path: 'partners',
        name: 'admin-partners',
        component: () => import('../pages/admin/PartnersPage.vue'),
      },
      {
        path: 'admins',
        name: 'admin-admins',
        component: () => import('../pages/admin/AdminsPage.vue'),
      },
      { path: 'llm', name: 'admin-llm', component: () => import('../pages/admin/LlmPage.vue') },
      {
        path: 'templates',
        name: 'admin-templates',
        component: () => import('../pages/admin/TemplatesPage.vue'),
      },
      { path: 'demo', name: 'admin-demo', component: () => import('../pages/admin/DemoPage.vue') },
      {
        path: 'support',
        name: 'admin-support',
        component: () => import('../pages/admin/SupportPage.vue'),
      },
      {
        path: 'announcements',
        name: 'admin-announcements',
        component: () => import('../pages/admin/AnnouncementsPage.vue'),
      },
    ],
    // Админка — не для владельцев клиентов: без этой проверки её страницы
    // монтируются и показывают человеку ошибки 403 вместо понятного отказа.
    meta: { admin: true },
  },
  { path: '/:pathMatch(.*)*', component: () => import('../pages/NotFoundPage.vue') },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
})

router.beforeEach(async (to) => {
  const session = useSessionStore()
  await session.load()

  if (to.meta.public) {
    // Демо-страницу показывают клиенту по ссылке: вошедшего администратора
    // уводить с неё в кабинет нельзя — он как раз и проверяет, что увидит
    // клиент.
    if (to.meta.anonymous) return true
    // Уже вошли — незачем держать на странице входа.
    if (!session.isAuthenticated) return true
    return session.totpPending ? '/totp' : '/app'
  }
  if (!session.isAuthenticated) {
    return { path: '/login', query: to.fullPath === '/' ? {} : { next: to.fullPath } }
  }
  // Половинчатая сессия: кроме ввода кода, кабинет всё равно ничего не отдаст.
  if (session.totpPending) return to.meta.totp ? true : '/totp'
  if (to.meta.totp) return '/app'
  // В админку пускаем только администратора сервиса. В режиме «войти как
  // клиент» — тоже нет: сессия принадлежит владельцу, и сервер её отвергнет.
  if (to.meta.admin && !(session.isAdmin && !session.impersonated)) return '/app'
  return true
})
